// packages/api-client/src/services/toolService.ts
import { getApiClient } from '../client';
import { handleApiError } from '../utils/handleApiError';
import type {
  ToolResponse,
  ToolCreatePayload,
  ToolUpdatePayload,
  ToolCodeResponse,
  ToolCodeUpdatePayload,
  ApiSuccessResponse,
  ServiceResponse,
  UUID,
  PaginatedResponse,
} from '@dopomogai/types';

export const toolService = {
  getAllTools: (): ServiceResponse<ToolResponse[]> => {
    return getApiClient().get<ToolResponse[]>('/tools')
      .then(response => response.data)
      .catch(err => handleApiError(err, 'fetching tools'));
  },

  getToolById: (toolUuid: UUID): ServiceResponse<ToolResponse> => {
    return getApiClient().get<ToolResponse>(`/tools/${toolUuid}`)
      .then(response => response.data)
      .catch(err => handleApiError(err, `fetching tool ${toolUuid}`));
  },

  createTool: (payload: ToolCreatePayload): ServiceResponse<ToolResponse> => {
    return getApiClient().post<ToolResponse>('/tools', payload)  // payload now includes code
      .then(response => response.data)
      .catch(err => handleApiError(err, 'creating tool'));
  },

  updateTool: (toolUuid: UUID, payload: ToolUpdatePayload): ServiceResponse<ToolResponse> => {
    return getApiClient().put<ToolResponse>(`/tools/${toolUuid}`, payload)
      .then(response => response.data)
      .catch(err => handleApiError(err, `updating tool metadata ${toolUuid}`));
  },

  deleteTool: (toolUuid: UUID): ServiceResponse<ApiSuccessResponse> => {
    return getApiClient().delete(`/tools/${toolUuid}`)
      // Add 'as const' to ensure TypeScript infers the literal type 'success'
      .then(() => ({ status: 'success' } as const))
      .catch(err => handleApiError(err, `deleting tool ${toolUuid}`));
  },


  getToolCode: (toolUuid: UUID): ServiceResponse<ToolCodeResponse> => {
    return getApiClient().get<ToolCodeResponse>(`/tools/${toolUuid}/code`)
      .then(response => response.data)
      .catch(err => handleApiError(err, `fetching code for tool ${toolUuid}`));
  },

  updateToolCode: (toolUuid: UUID, payload: ToolCodeUpdatePayload): ServiceResponse<ToolCodeResponse> => {
    return getApiClient().put<ToolCodeResponse>(`/tools/${toolUuid}/code`, payload)
      .then(response => response.data)
      .catch(err => handleApiError(err, `updating code for tool ${toolUuid}`));
  },

  enableTool: (toolUuid: UUID): ServiceResponse<ToolResponse> => {
    return toolService.updateTool(toolUuid, { enabled: true });
  },

  disableTool: (toolUuid: UUID): ServiceResponse<ToolResponse> => {
    return toolService.updateTool(toolUuid, { enabled: false });
  },
};